import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface ErrorData {
  error: Error;
  componentStack?: string;
  eventId?: string;
  resetError?: () => void;
}

export interface ChartDataFetchErrorStyles {
  backgroundColor?: string;
  textColor?: string;
  secondaryTextColor?: string;
  buttonColor?: string;
  buttonHoverColor?: string;
  errorIconColor?: string;
  fontSize?: string;
  compact?: boolean;
}

interface ChartDataFetchErrorProps {
  errorData?: ErrorData;
  retry?: () => void;
  styles?: ChartDataFetchErrorStyles;
}

const Container = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const InnerContainer = styled.div<{ $backgroundColor?: string }>`
  margin: 0;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: ${(props) => props.$backgroundColor || '#f8fafc'};
  border-radius: 0.5rem;

  .dark & {
    background: ${(props) => props.$backgroundColor || '#1e293b'};
  }
`;

const CenteredText = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.125rem; /* Reduced gap for more compact layout */
`;

const StyledErrorIcon = styled(ErrorIcon)<{
  $errorIconColor?: string;
  $compact?: boolean;
}>`
  margin-left: auto;
  margin-right: auto;
  height: ${(props) =>
    props.$compact ? '1rem' : '1.25rem'}; /* Smaller icon for compact mode */
  width: ${(props) => (props.$compact ? '1rem' : '1.25rem')};
  color: ${(props) => props.$errorIconColor || '#ef4444'};

  .dark & {
    color: ${(props) => props.$errorIconColor || '#f87171'};
  }
`;

const ErrorMessage = styled.div<{ $textColor?: string; $fontSize?: string }>`
  margin: 0;
  font-size: ${(props) => props.$fontSize || '0.75rem'};
  font-weight: 500;
  color: ${(props) => props.$textColor || '#0f172a'};
  line-height: 1.3;

  p {
    margin: 0;
    &:not(:last-child) {
      margin-bottom: 0.125rem;
    }
  }

  .dark & {
    color: ${(props) => props.$textColor || '#f8fafc'};
  }
`;

const AdditionalMessage = styled.div<{
  $secondaryTextColor?: string;
  $fontSize?: string;
}>`
  font-size: ${(props) => props.$fontSize || '0.75rem'};
  color: ${(props) => props.$secondaryTextColor || '#64748b'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  .dark & {
    color: ${(props) => props.$secondaryTextColor || '#94a3b8'};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledIconButton = styled(IconButton)<{
  $buttonColor?: string;
  $buttonHoverColor?: string;
  $fontSize?: string;
  $compact?: boolean;
}>`
  && {
    color: ${(props) => props.$buttonColor || '#3b82f6'};
    padding: ${(props) =>
      props.$compact
        ? '0.125rem'
        : '0.25rem'}; /* Reduced padding for compact mode */
    font-size: ${(props) => props.$fontSize || '0.75rem'};
    font-weight: 500;
    border-radius: 0.25rem;
    background: transparent;

    &:hover {
      background: ${(props) =>
        props.$buttonHoverColor || 'rgba(59, 130, 246, 0.1)'};
    }

    .dark & {
      color: ${(props) => props.$buttonColor || '#60a5fa'};

      &:hover {
        background: ${(props) =>
          props.$buttonHoverColor || 'rgba(96, 165, 250, 0.1)'};
      }
    }
  }
`;

const MIN_LOADING_TIME = 500; // minimum time to show loading state in ms

export const ChartDataFetchError = (props: ChartDataFetchErrorProps) => {
  const { t } = useTranslation();
  const { retry, errorData, styles } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleRetry = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

    // Always wait for the minimum time, even if the operation was faster
    await new Promise(() =>
      setTimeout(() => {
        errorData?.resetError?.();
        retry?.();
        setIsLoading(false);
      }, remainingTime)
    );
  };

  console.error(errorData);

  const isCompact = styles?.compact;
  const fontSize = styles?.fontSize;

  return (
    <Container>
      <InnerContainer $backgroundColor={styles?.backgroundColor}>
        <CenteredText>
          <StyledErrorIcon
            aria-hidden={true}
            $errorIconColor={styles?.errorIconColor}
            $compact={isCompact}
          />
          <ErrorMessage $textColor={styles?.textColor} $fontSize={fontSize}>
            <p>
              {isCompact
                ? t('common:errors.dataLoadingErrorCompact')
                : t('common:errors.dataLoadingErrorNormal')}
            </p>
          </ErrorMessage>
          <AdditionalMessage
            $secondaryTextColor={styles?.secondaryTextColor}
            $fontSize={fontSize}
          >
            {retry ? (
              <ButtonContainer>
                <StyledIconButton
                  aria-label="refresh"
                  onClick={handleRetry}
                  disabled={isLoading}
                  $buttonColor={styles?.buttonColor}
                  $buttonHoverColor={styles?.buttonHoverColor}
                  $fontSize={fontSize}
                  $compact={isCompact}
                >
                  {isLoading ? (
                    t('common:actions.loading')
                  ) : (
                    <>
                      <RefreshIcon fontSize={isCompact ? 'inherit' : 'small'} />{' '}
                      {t('common:actions.tryAgain')}
                    </>
                  )}
                </StyledIconButton>
              </ButtonContainer>
            ) : null}
          </AdditionalMessage>
        </CenteredText>
      </InnerContainer>
    </Container>
  );
};
